<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { RouterView } from 'vue-router';

import { UErrorView, useError } from '@/shared/ui';

import { useAppBadge } from '@/shared/composables/useAppBadge';
import { useAuth } from '@/shared/composables/useAuth';
import {
  provideNotifications,
  useNotifications,
} from '@/shared/composables/useNotifications';
import { useSwNavigationListener } from '@/shared/composables/useSwNavigationListener';
import { useTheme } from '@/shared/composables/useTheme';
import { useVersionCheck } from '@/shared/composables/useVersionCheck';

import type { NavigationMenuItem } from '@nuxt/ui';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

const { currentUser, isGranted, logout } = useAuth();

// Instantiate the notifications state once at the layout root and share it with
// every descendant (sidebar badge, /inbox page). This is the sole provider, and
// it must live above the sidebar: anything rendered only when the sidebar is
// expanded would tear down the live subscription and badge on collapse.
const notifications = useNotifications();
provideNotifications(notifications);
const { loadedUnreadCount } = notifications;

// PWA: keep the eternally-open window on the latest release, route
// service-worker notification clicks that reach uncontrolled windows, and mirror
// the inbox unread count onto the installed app's dock/taskbar icon.
useVersionCheck();
useSwNavigationListener();
useAppBadge(loadedUnreadCount);

const open = ref(false);
const hovered = ref(false);
const keyboardFocused = ref(false);
const userMenuOpen = ref(false);
useEventListener('pointerdown', () => {
  keyboardFocused.value = false;
});
watch(userMenuOpen, (value) => {
  if (!value) hovered.value = false;
});
const compact = computed(() => !hovered.value && !keyboardFocused.value && !userMenuOpen.value);

function onSidebarFocus(event: FocusEvent) {
  if ((event.target as HTMLElement).matches(':focus-visible')) {
    keyboardFocused.value = true;
  }
}

function onSidebarBlur(event: FocusEvent) {
  if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
    keyboardFocused.value = false;
  }
}
const { preference } = useTheme();

// Views whose component instance survives navigation into a detail page, so
// scroll position and local view state are restored on the way back instead of
// remounting from scratch. Matched against the component `name` each page
// declares via defineOptions.
const CACHED_PAGES = ['InboxPage'];

const navigationItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [];

  if (isGranted('omb:core:users:list')) {
    items.push({
      label: 'Users',
      icon: 'i-lucide-users',
      to: '/users',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  return items;
});

const navigationUi = {
  root: 'gap-0',
  list: 'flex flex-col gap-1',
  link: 'h-8 gap-2 text-[13px] font-normal rounded-md px-2 py-1.5 before:inset-0',
  linkLeadingIcon: 'shrink-0 size-4',
  linkBadge: 'h-4 min-w-4 justify-center rounded px-1 text-[10px]',
  linkLabel: 'truncate',
};

// The rail never switches the menu into Nuxt UI's `collapsed` mode: that swaps
// every item's component, so a link focused by Shift-Tab (which is what expands
// the rail) is unmounted and focus falls to <body>. The nodes stay put instead
// and only the label and badge are hidden while compact. The label stays in the
// accessibility tree, so an icon-only link still reads as "Users". Tooltips
// are not needed: hovering the rail expands it.
const railNavigationUi = computed(() => ({
  ...navigationUi,
  linkLabel: compact.value ? 'truncate sr-only' : navigationUi.linkLabel,
  linkTrailing: compact.value ? 'hidden' : undefined,
}));

const userMenuItems = computed(() => [[{
  label: 'My profile',
  icon: 'i-lucide-user',
  to: '/me/edit',
}], [{
  label: 'Appearance',
  icon: 'i-lucide-sun-moon',
  children: (['light', 'dark', 'system'] as const).map((value) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1),
    type: 'checkbox' as const,
    checked: preference.value === value,
    onSelect: () => { preference.value = value; },
  })),
}], [{
  label: 'Sign out',
  icon: 'i-lucide-log-out',
  onSelect: () => logout(),
}]]);
</script>

<template>
  <!-- Critical error: hide entire layout, show only error -->
  <template v-if="hasError && isCriticalError">
    <UErrorView />
  </template>

  <!-- Normal layout -->
  <template v-else>
    <div
      class="hidden w-12 shrink-0 lg:block"
      aria-hidden="true"
    />
    <UDashboardSidebar
      id="main"
      v-model:open="open"
      class="omb-sidebar bg-muted border-0"
      :class="{ 'is-expanded': !compact }"
      :ui="{ header: 'px-2 gap-2', body: 'px-2 py-2' }"
      @pointerenter="hovered = true"
      @pointerleave="hovered = false"
      @focusin="onSidebarFocus"
      @focusout="onSidebarBlur"
    >
      <template #header>
        <UDropdownMenu
          v-model:open="userMenuOpen"
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
          class="w-full min-w-0"
        >
          <UButton
            color="neutral"
            variant="ghost"
            class="w-full"
            data-testid="sidebar-user-button"
            :ui="{ base: 'h-8 gap-2 px-1.5 py-1', trailingIcon: 'ms-auto size-3.5 text-dimmed' }"
            :trailing-icon="compact ? undefined : 'i-lucide-chevron-down'"
          >
            <UAvatar
              :src="currentUser.avatar?.renditions?.[0]?.url"
              :alt="currentUser.fullName"
              size="2xs"
            />
            <!-- Always rendered: the compact rail only hides it visually, so
                 the button keeps the user's name as its label. -->
            <span
              class="truncate text-sm font-medium text-highlighted"
              :class="{ 'sr-only': compact }"
              data-testid="sidebar-user-name"
            >
              {{ currentUser.fullName }}
            </span>
          </UButton>
        </UDropdownMenu>
      </template>

      <template #default>
        <UNavigationMenu
          :items="navigationItems"
          :ui="railNavigationUi"
          color="neutral"
          orientation="vertical"
          aria-label="Main navigation"
        />
      </template>
    </UDashboardSidebar>

    <!-- min-w-0 lets the panel shrink to the free space next to the sidebar;
         without it wide content (e.g. a wide table) pushes the layout past
         the viewport instead of scrolling inside its own container. -->
    <div class="omb-workspace min-w-0 flex-1 min-h-0 flex flex-col bg-default">
      <!-- Non-critical error: show within layout -->
      <UErrorView v-if="hasError" />
      <RouterView
        v-else
        v-slot="{ Component }"
      >
        <KeepAlive :include="CACHED_PAGES">
          <component :is="Component" />
        </KeepAlive>
      </RouterView>
    </div>
  </template>
</template>

<style>
.omb-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  width: 3rem;
  min-width: 3rem;
  transition: width 160ms ease;
}

.omb-sidebar.is-expanded {
  width: 14rem;
  box-shadow: 8px 0 24px rgb(0 0 0 / 12%);
  border-right: 1px solid var(--ui-border-muted);
}

.omb-workspace {
  margin: 8px 8px 8px 0;
  height: calc(100svh - 16px);
  overflow: hidden;
  border: 1px solid var(--ui-border-muted);
  border-radius: 10px;
}
@media (prefers-reduced-motion: reduce) {
  .omb-sidebar { transition: none; }
}
</style>
